import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { resolveMediaUrl } from '../services/productsApi';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDate = () =>
  new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date());

const buildAttributesHtml = (attributes = []) => {
  if (!attributes.length) return '';
  return `
    <ul class="quote-pdf-attrs">
      ${attributes
        .map(
          (attr) => `
        <li>
          <strong>${escapeHtml(attr.attributeLabel)}:</strong>
          ${escapeHtml(attr.optionLabel)}
        </li>`
        )
        .join('')}
    </ul>
  `;
};

const buildItemsHtml = (items = []) =>
  items
    .map((item) => {
      const imageSrc = resolveMediaUrl(item.images?.[0]);
      return `
        <article class="quote-pdf-item">
          <div class="quote-pdf-item-media">
            ${
              imageSrc
                ? `<img src="${escapeHtml(imageSrc)}" alt="" crossorigin="anonymous" />`
                : '<div class="quote-pdf-item-placeholder"></div>'
            }
          </div>
          <div class="quote-pdf-item-body">
            <p class="quote-pdf-item-category">${escapeHtml(item.category || '')}</p>
            <h3>${escapeHtml(item.name || 'Producto')}</h3>
            ${buildAttributesHtml(item.selectedAttributes)}
            ${
              item.description
                ? `<p class="quote-pdf-item-desc">${escapeHtml(item.description)}</p>`
                : ''
            }
          </div>
          <div class="quote-pdf-item-qty">
            <span>Cantidad</span>
            <strong>${escapeHtml(item.quantity)}</strong>
          </div>
        </article>
      `;
    })
    .join('');

const buildTemplateHtml = ({ items, client }) => {
  const nombre = client?.nombre?.trim() || 'No proporcionado';
  const correo = client?.correo?.trim() || 'No proporcionado';
  const telefono = client?.telefono?.trim() || 'No proporcionado';
  const comentario = client?.comentario?.trim() || 'Sin comentarios adicionales.';

  return `
    <div class="quote-pdf-root">
      <header class="quote-pdf-header">
        <img src="/assets/Logos/logo.png" alt="VERISSA" class="quote-pdf-logo" />
        <div class="quote-pdf-meta">
          <p class="quote-pdf-doc-title">Solicitud de cotización</p>
          <p>${escapeHtml(formatDate())}</p>
        </div>
      </header>

      <section class="quote-pdf-client">
        <h2>Datos del cliente</h2>
        <div class="quote-pdf-client-grid">
          <div><span>Nombre</span><strong>${escapeHtml(nombre)}</strong></div>
          <div><span>Correo</span><strong>${escapeHtml(correo)}</strong></div>
          <div><span>Teléfono</span><strong>${escapeHtml(telefono)}</strong></div>
        </div>
        <div class="quote-pdf-comment">
          <span>Comentario</span>
          <p>${escapeHtml(comentario)}</p>
        </div>
      </section>

      <section class="quote-pdf-products">
        <div class="quote-pdf-section-head">
          <h2>Productos a cotizar</h2>
          <p>${items.length} ${items.length === 1 ? 'producto' : 'productos'}</p>
        </div>
        ${buildItemsHtml(items)}
      </section>

      <footer class="quote-pdf-footer">
        <p>Este documento es una solicitud de cotización. Los precios y disponibilidad se confirmarán por el equipo VERISSA.</p>
        <p>verissa.mx</p>
      </footer>
    </div>
  `;
};

const TEMPLATE_STYLES = `
  .quote-pdf-root {
    width: 794px;
    box-sizing: border-box;
    padding: 40px 44px 48px;
    background: #ffffff;
    color: #1f1f1f;
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  }
  .quote-pdf-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    padding-bottom: 18px;
    border-bottom: 2px solid #8B2635;
  }
  .quote-pdf-logo {
    display: block;
    height: 54px;
    width: auto;
    object-fit: contain;
  }
  .quote-pdf-meta {
    text-align: right;
  }
  .quote-pdf-doc-title {
    margin: 0 0 4px;
    font-size: 16px;
    font-weight: 700;
    color: #1f1f1f;
  }
  .quote-pdf-meta p {
    margin: 0;
    font-size: 12px;
    color: #6b6b6b;
  }
  .quote-pdf-client,
  .quote-pdf-products {
    margin-top: 28px;
  }
  .quote-pdf-client h2,
  .quote-pdf-products h2 {
    margin: 0 0 12px;
    font-size: 14px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #8B2635;
  }
  .quote-pdf-client-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }
  .quote-pdf-client-grid div,
  .quote-pdf-comment {
    background: #f8f8f8;
    border-radius: 10px;
    padding: 12px 14px;
  }
  .quote-pdf-client-grid span,
  .quote-pdf-comment span,
  .quote-pdf-item-qty span {
    display: block;
    margin-bottom: 4px;
    font-size: 11px;
    color: #6b6b6b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .quote-pdf-client-grid strong {
    font-size: 13px;
    font-weight: 600;
    word-break: break-word;
  }
  .quote-pdf-comment {
    margin-top: 12px;
  }
  .quote-pdf-comment p {
    margin: 0;
    font-size: 13px;
    line-height: 1.45;
  }
  .quote-pdf-section-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 10px;
  }
  .quote-pdf-section-head p {
    margin: 0;
    font-size: 12px;
    color: #6b6b6b;
  }
  .quote-pdf-item {
    display: grid;
    grid-template-columns: 180px 1fr 78px;
    gap: 18px;
    align-items: center;
    padding: 16px 0;
    border-bottom: 1px solid #e8e8e8;
  }
  .quote-pdf-item-media {
    width: 180px;
    height: 180px;
    box-sizing: border-box;
    padding: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    overflow: hidden;
    background: #fff;
    border: 1px solid #ececec;
  }
  .quote-pdf-item-media img,
  .quote-pdf-item-placeholder {
    display: block;
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  .quote-pdf-item-body h3 {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 700;
  }
  .quote-pdf-item-category {
    margin: 0 0 4px;
    font-size: 11px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #8B2635;
    font-weight: 600;
  }
  .quote-pdf-attrs {
    margin: 0 0 8px;
    padding-left: 16px;
    font-size: 12px;
    color: #444;
  }
  .quote-pdf-attrs li {
    margin-bottom: 2px;
  }
  .quote-pdf-item-desc {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: #555;
  }
  .quote-pdf-item-qty {
    text-align: right;
  }
  .quote-pdf-item-qty strong {
    font-size: 20px;
    color: #8B2635;
  }
  .quote-pdf-footer {
    margin-top: 28px;
    padding-top: 16px;
    border-top: 1px solid #e8e8e8;
    font-size: 11px;
    color: #6b6b6b;
    line-height: 1.5;
  }
  .quote-pdf-footer p {
    margin: 0 0 4px;
  }
`;

const QUOTE_IMAGE_INNER = 152;

const fitQuoteImages = (root) => {
  root.querySelectorAll('.quote-pdf-item-media img').forEach((img) => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!width || !height) return;

    const scale = Math.min(QUOTE_IMAGE_INNER / width, QUOTE_IMAGE_INNER / height);
    img.style.width = `${Math.max(1, Math.round(width * scale))}px`;
    img.style.height = `${Math.max(1, Math.round(height * scale))}px`;
  });
};

const waitForImages = (root) => {
  const images = Array.from(root.querySelectorAll('img'));
  return Promise.all(
    images.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        })
    )
  );
};

export const downloadQuotePdf = async ({ items, client }) => {
  if (!items?.length) return;

  const host = document.createElement('div');
  host.style.position = 'fixed';
  host.style.left = '-10000px';
  host.style.top = '0';
  host.style.zIndex = '-1';
  host.setAttribute('aria-hidden', 'true');

  const style = document.createElement('style');
  style.textContent = TEMPLATE_STYLES;
  host.appendChild(style);

  const content = document.createElement('div');
  content.innerHTML = buildTemplateHtml({ items, client });
  host.appendChild(content);
  document.body.appendChild(host);

  try {
    await waitForImages(host);
    fitQuoteImages(host);

    const root = host.querySelector('.quote-pdf-root');
    const canvas = await html2canvas(root, {
      scale: 2,
      useCORS: true,
      allowTaint: false,
      backgroundColor: '#ffffff',
      logging: false,
      imageTimeout: 8000,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF('p', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const stamp = new Date().toISOString().slice(0, 10);
    pdf.save(`cotizacion-verissa-${stamp}.pdf`);
  } finally {
    host.remove();
  }
};
